import { Button, Logotype } from '@sortlist-frontend/design-system';
import { HttpException } from '@sortlist-frontend/utils';

import { useSharedComponentsContext } from '../utils';

type ErrorPageProps = {
  statusCode?: number | string | null;
  error?: Error | HttpException | null;
  message?: string;
  sentryErrorId?: string;
  displayDebug?: boolean;
  onRefresh?: () => void;
  onLogout?: () => void;
  children?: never;
};

const ErrorDebugPanel = (props: { error: ErrorPageProps['error'] }) => {
  const { error: e } = props;

  const debugMap = [
    ['error.message', e?.message],
    ['error.name', e?.name],
    ['error.class', e instanceof Error ? e.constructor.name : undefined],
    ['http.url', e instanceof HttpException ? e.url : undefined],
    ['http.status', e instanceof HttpException ? e.status : undefined],
    ['http.previous', e instanceof HttpException ? e.previousError?.message : undefined],
    ['error.stack', e instanceof Error ? e.stack : undefined],
  ];

  return (
    <table data-testid="debug">
      {debugMap.map(([key, value]) => {
        if (value !== undefined) {
          return (
            <tr key={key}>
              <td>
                <strong>{key}</strong>
              </td>
              <td>{value}</td>
            </tr>
          );
        }
      })}
    </table>
  );
};

export const ErrorPage = (props: ErrorPageProps) => {
  const { statusCode, message, error, displayDebug = false, sentryErrorId, onRefresh, onLogout } = props;
  const { t } = useSharedComponentsContext();

  // A 404 won't be fixed by reloading the same missing page, so send the user home instead.
  const isNotFound = statusCode === 404 || statusCode === '404';
  const primaryAction = isNotFound
    ? () => window.location.assign('/')
    : (onRefresh ?? (() => window.location.reload()));

  return (
    <div
      data-testid="error-page"
      className="relative overflow-hidden w-full h-full flex flex-col items-center justify-start p-8 bg-gradient-to-t from-primary-100 to-50% to-white">
      <Logotype variant="solid" className="h-6 z-10 [&>path]:fill-primary-600 fill-primary-600" />
      <div className="flex flex-col justify-center items-center p-4 flex-1 z-20">
        <h1 className="font-bold text-secondary-100 text-9xl sm:text-[20rem] bg-gradient-to-b from-primary-300 to-85% to-transparent bg-clip-text text-transparent">
          {statusCode ?? t('base:errorPage.noStatusHeadline')}
        </h1>
        <h2 data-testid="title" className="text-xl font-semibold">
          {t('base:errorPage.title')}
        </h2>
        <p data-testid="message" className="text-secondary-500 mt-2 text-sm">
          {message ?? t('base:errorPage.default')}
        </p>
        {sentryErrorId != null && (
          <p className="text-secondary-400 mt-6 text-xs" data-testid="sentry-id">
            Error id: {sentryErrorId}
          </p>
        )}
        <div className="flex flex-col gap-4 mt-8 justify-start items-center">
          <Button
            variant="solid"
            size="sm"
            color="primary"
            hasShadow
            data-testid={isNotFound ? 'home-link' : 'refresh-link'}
            onClick={primaryAction}>
            {isNotFound ? t('base:errorPage.backToHome') : t('base:errorPage.refresh')}
          </Button>
          {onLogout != null && (
            <Button
              variant="light"
              size="xs"
              color="primary"
              className="underline"
              data-testid="logout-link"
              onClick={onLogout}>
              {t('base:errorPage.logout')}
            </Button>
          )}
        </div>
      </div>
      {displayDebug && <ErrorDebugPanel error={error} />}
      <div className="p-96 bg-gradient-to-b from-primary-300 to-transparent absolute top-0 -translate-y-1/2 rounded-full z-0 opacity-50">
        <div className="p-64 bg-gradient-to-b from-primary-300 to-transparent rounded-full">
          <div className="w-96 h-96 rounded-full bg-gradient-to-b from-primary-400 to-transparent" />
        </div>
      </div>
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 h-full w-full opacity-[0.35] mix-blend-multiply">
        <filter id="error-page-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="4" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#error-page-noise)" />
      </svg>
    </div>
  );
};
